import { Box, Divider, Typography } from "@mui/material";

/**
 * PageSection — card-surface content wrapper for page sections
 *
 * title       — section heading (optional)
 * subtitle    — supporting text below title (optional)
 * action      — element rendered top-right (e.g. a Button)
 * divider     — show divider below header (default true when title is present)
 * noPad       — removes body padding (for tables that need edge-to-edge content)
 * compact     — tighter header padding
 */
export default function PageSection({
  title,
  subtitle,
  action,
  divider,
  noPad = false,
  compact = false,
  children,
  sx,
}) {
  const showDivider = divider ?? !!title;
  const headerPad   = compact ? "14px 20px" : "20px 24px";
  const bodyPad     = compact ? "16px 20px" : "20px 24px";

  return (
    <Box
      sx={{
        background: "var(--surface-card)",
        border: "1px solid var(--line-100)",
        borderRadius: "var(--r-lg)",
        boxShadow: "var(--shadow-card)",
        overflow: "hidden",
        ...sx,
      }}
    >
      {(title || action) && (
        <>
          <Box sx={{
            p: headerPad,
            display: "flex",
            alignItems: subtitle ? "flex-start" : "center",
            justifyContent: "space-between",
            gap: 2,
          }}>
            {title && (
              <Box>
                <Typography sx={{
                  fontFamily: "var(--font-display)",
                  fontWeight: 700,
                  fontSize: compact ? "var(--t-body)" : "var(--t-h4)",
                  color: "var(--ink-900)",
                  letterSpacing: "-0.01em",
                  lineHeight: 1.3,
                }}>
                  {title}
                </Typography>
                {subtitle && (
                  <Typography sx={{
                    mt: "3px",
                    fontSize: "var(--t-body-sm)",
                    color: "var(--ink-500)",
                    lineHeight: 1.5,
                  }}>
                    {subtitle}
                  </Typography>
                )}
              </Box>
            )}
            {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
          </Box>

          {showDivider && <Divider sx={{ borderColor: "var(--line-100)" }} />}
        </>
      )}

      {noPad ? children : (
        <Box sx={{ p: bodyPad }}>
          {children}
        </Box>
      )}
    </Box>
  );
}
